'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import AuthBar from '@/components/AuthBar';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';

export default function RegisterPage() {
  const { language, user } = useAppStore();
  const t = translate(language);
  const { register, sendCode } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const error = (register.error || sendCode.error) as Error | null;

  useEffect(() => {
    if (countdown <= 0) return;
    const id = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  const requestCode = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError(language !== 'en' ? '请先输入有效的信箱地址' : 'Enter a valid email first');
      return;
    }
    setLocalError('');
    sendCode.mutate(
      { email, purpose: 'register' },
      {
        onSuccess: (res) => {
          setCountdown(res.retryAfter || 60);
        },
      },
    );
  };

  if (user) {
    return (
      <div className="mx-auto w-full max-w-[420px] px-6 pt-16">
        <AuthBar />
        <div className="card p-8 text-center">
          <h1 className="mb-3 text-xl font-bold">{t.auth.alreadyLoggedIn}</h1>
          <div className="flex flex-col gap-2">
            <Link href="/markets" className="btn-ghost !h-10">
              {t.auth.backToMarkets}
            </Link>
            <Link href="/profile" className="btn-primary !h-10">
              {t.auth.goToAccount}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[420px] px-6 pt-4 sm:pt-6">
      <AuthBar />
      <div className="card p-6 sm:p-8">
        <h1 className="mb-6 text-center text-2xl font-bold">{t.auth.createAccount}</h1>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (password !== confirm) {
              setLocalError(language !== 'en' ? '两次输入的密码不一致' : 'Passwords do not match');
              return;
            }
            if (!code) {
              setLocalError(language !== 'en' ? '请输入验证码' : 'Enter the verification code');
              return;
            }
            setLocalError('');
            register.mutate({ email, password, code });
          }}
        >
          <div>
            <label className="mb-1 block text-sm text-gf-muted">{t.auth.email}</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input flex-1 !h-10 !text-base"
                autoComplete="email"
                placeholder={t.auth.emailPlaceholder}
                required
              />
              <button
                type="button"
                className="btn-ghost whitespace-nowrap !h-10 !px-3 text-xs"
                onClick={requestCode}
                disabled={sendCode.isPending || countdown > 0}
              >
                {countdown > 0
                  ? t.auth.resendIn.replace('{s}', String(countdown))
                  : sendCode.isPending
                    ? t.common.loading
                    : t.auth.sendCode}
              </button>
            </div>
            {sendCode.isSuccess && !countdown && (
              <p className="mt-1 text-xs text-gf-positive">{t.auth.codeSent}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm text-gf-muted">{t.auth.verificationCode}</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input !h-10 !text-base"
              placeholder={t.auth.codePlaceholder}
              inputMode="numeric"
              maxLength={6}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gf-muted">{t.auth.password}</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input !h-10 !pr-10 !text-base"
                autoComplete="new-password"
                placeholder={t.auth.passwordPlaceholder}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1.5 py-1 text-xs text-gf-muted hover:text-gf-text"
                aria-label="Show password"
              >
                {showPwd ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gf-muted">{t.auth.confirmPassword}</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input !h-10 !pr-10 !text-base"
                autoComplete="new-password"
                placeholder={t.auth.confirmPasswordPlaceholder}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1.5 py-1 text-xs text-gf-muted hover:text-gf-text"
                aria-label="Show password"
              >
                {showConfirm ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          <p className="text-xs text-gf-muted">{t.auth.randomUsernameHint}</p>
          {(error || localError) && (
            <div className="text-sm text-gf-negative">{localError || error!.message}</div>
          )}
          <button type="submit" className="btn-primary !h-10" disabled={register.isPending}>
            {register.isPending ? t.auth.registering : t.auth.register}
          </button>
        </form>
        <div className="mt-4 text-center text-sm text-gf-muted">
          <a href="/login" className="text-gf-accent hover:underline">
            {t.auth.hasAccount}
          </a>
        </div>
      </div>
    </div>
  );
}
